// components/reflect-column.js
// ═══════════════════════════════════════════════════════════════
// "Reflect with <ai_name>" — private 1:1 thinking surface inside
// a multi-user Collaborate room.
//
// Uses the SAME chat plumbing as the regular Collaborate thread
// (useChatThread + CollaborateMessage + ChatInputBar) so the
// experience is identical to the main room — only the backend
// differs: requests go to /api/space-session-lens-chat which
// persists to lens_messages (private per user, separate from the
// shared messages table, no intelligence pipelines fire).
//
// Privacy promise: the other participant never sees this thread.
// Lens chat is the user's private channel with their personal AI.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import ChatInputBar from './chat-input-bar';
import HighlightToolbar from './highlight-toolbar';
import { CollaborateMessage } from './collaborate-message';
import { useChatThread } from '../hooks/use-chat-thread';
import { useVoice } from '../hooks/use-voice';

export default function ReflectColumn({ workspaceId, supabase, userId, userRecord, voiceConfig, workspaceSession }) {
  const aiName = userRecord?.ai_name || 'Shift';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [deepModeArmed, setDeepModeArmed] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const inputRef = useRef(null);
  const voiceSourceRef = useRef(null);
  const voiceResponseCallbackRef = useRef(null);

  // ── Load existing lens_messages on mount ──
  useEffect(() => {
    if (!workspaceId || !supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const res = await fetch(
          `/api/space-session-lens-load?spaceId=${encodeURIComponent(workspaceId)}`,
          { headers: { 'Authorization': `Bearer ${authSession?.access_token || ''}` } },
        );
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setMessages(data.messages || []);
        }
      } catch (e) {
        console.debug('[reflect-column] load failed:', e.message);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, supabase]);

  // ── Wire useChatThread to the lens-chat endpoint ──
  // beforeSend just stamps a sender_id so CollaborateMessage's
  // attribution check (m.sender_id === userId) renders correctly.
  // buildRequest hits the lens-chat SSE endpoint — same protocol
  // as /api/chat (data: { t } / data: { done, text, assistantMsgId }).
  const chat = useChatThread({
    messages,
    setMessages,
    typingAnimation: true,
    inputRef,
    beforeSend: async ({ text }) => {
      const fileToSend = pendingFile;
      setPendingFile(null);
      return {
        userMsgExtras: {
          sender_id: userId,
          ...(fileToSend?.isImage ? { isImage: true, imageData: fileToSend.base64, mimeType: fileToSend.type, imageName: fileToSend.name } : {}),
        },
        fileData: fileToSend ? {
          base64: fileToSend.base64,
          name:   fileToSend.name,
          type:   fileToSend.type,
          isImage: fileToSend.isImage,
          isPDF:   fileToSend.isPDF,
        } : null,
      };
    },
    buildRequest: async ({ text, fileData, messagesSnapshot }) => {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      const wasDeepMode = deepModeArmed;
      if (wasDeepMode) setDeepModeArmed(false); // one-shot
      const payload = messagesSnapshot
        .map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : '' }))
        .filter(m => m.content.trim());
      // Last message (just-pushed user msg) becomes the live user input —
      // useChatThread already added it to the snapshot, so the endpoint
      // receives the full conversation including the new turn.
      return {
        url: '/api/space-session-lens-chat',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        },
        body: {
          spaceId: workspaceId,
          messages: payload,
          ...(fileData ? { fileData } : {}),
          ...(wasDeepMode ? { deepMode: true } : {}),
        },
      };
    },
    onResponseDone: () => { /* lens-chat persists server-side; nothing to do here */ },
  });

  const {
    scrollContainerRef,
    lastUserMsgRef,
    spacerRef,
    loading,
    send: chatSend,
    abort,
  } = chat;

  // Voice — same plumbing as the regular Collaborate panel. If
  // voiceConfig is missing the voice button hides automatically
  // (ChatInputBar checks `typeof onVoiceToggle === 'function'`).
  const voice = useVoice({
    session: voiceConfig?.session || null,
    userRecord,
    currentSessionId: workspaceId,
    threadHistory: voiceConfig?.threadHistory || [],
    voiceEnabled: voiceConfig?.voiceEnabled ?? true,
    inputRef,
    setInput: (val) => {
      setInput(val);
      if (inputRef?.current) inputRef.current.innerText = val;
    },
    send: () => submitMessage(),
    voiceSourceRef,
    voiceResponseCallbackRef,
  });
  const { voiceListening = false, voiceEnabled = true, toggleVoice = () => {} } = voice || {};

  // Most-recent user message index — for CollaborateMessage's lastUser flag
  const lastUserIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === 'user') return i;
    return -1;
  })();

  function submitMessage(directText) {
    const text = (directText !== undefined ? directText : input).trim();
    if (!text && !pendingFile) return;
    chatSend(text);
    setInput('');
    if (inputRef.current) inputRef.current.innerText = '';
  }

  function handleFilePickerSelect(file) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setPendingFile({
        base64,
        name: file.name,
        type: file.type,
        isImage: file.type.startsWith('image/'),
        isPDF:   file.type === 'application/pdf',
      });
    };
    reader.readAsDataURL(file);
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minWidth: 0,
      background: '#07070a',
    }}>
      {/* Message list — same shape as the regular Collaborate thread.
          Privacy subtitle now sits inline with the REFLECT tab in
          the parent (workstation-layer2.js) header, not here. */}
      <div
        ref={scrollContainerRef}
        className="ws-chat-scroll"
        style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
      >
        <div style={{ padding: '14px 16px 0 16px' }}>
          {loadingHistory && (
            <div style={{ color: '#555', fontSize: 11, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', marginTop: 20 }}>
              Loading…
            </div>
          )}
          {!loadingHistory && messages.length === 0 && (
            <div style={{ color: '#555', fontSize: 12, fontStyle: 'italic', fontFamily: 'Georgia, serif', textAlign: 'center', marginTop: 40, lineHeight: 1.7, padding: '0 20px' }}>
              Sit with what just happened in the room. Anything you say
              here stays private — {aiName} can see the shared conversation
              but won't repeat your reflections back to anyone.
            </div>
          )}
          {messages.map((m, i) => (
            <CollaborateMessage
              key={m.id || i}
              m={m} i={i}
              isLastUser={i === lastUserIndex}
              lastUserMsgRef={lastUserMsgRef}
              userRecord={userRecord}
              userId={userId}
              otherNames={workspaceSession?.otherNames || {}}
            />
          ))}
          <div ref={spacerRef} style={{ height: 0, flexShrink: 0 }} aria-hidden="true" />
        </div>
      </div>

      {/* Same ChatInputBar component the regular thread uses. No
          askShift toggle here — Reflect IS the user's private chat.
          No deep search either (lens chat handles search differently).
          Voice + deep mode are wired so the input bar matches the
          regular thread's controls. */}
      <ChatInputBar
        inputRef={inputRef}
        value={input}
        setValue={setInput}
        onSend={(merged) => submitMessage(merged)}
        loading={loading}
        onAbort={abort}
        pendingFile={pendingFile}
        setPendingFile={setPendingFile}
        onFileSelect={handleFilePickerSelect}
        deepModeArmed={deepModeArmed}
        setDeepModeArmed={setDeepModeArmed}
        voiceListening={voiceListening}
        voiceEnabled={voiceEnabled}
        onVoiceToggle={() => {
          const next = !voiceEnabled;
          voiceConfig?.onVoiceToggle?.(next);
          if (next) toggleVoice();
          else if (voiceListening) toggleVoice();
        }}
        placeholder={`Reflect with ${aiName}…`}
      />

      {/* Text-selection toolbar — capture observations from the
          Reflect thread into your private notes. */}
      <HighlightToolbar
        userId={userId}
        supabase={supabase}
        sessionId={workspaceId}
        onReply={(text) => {
          const quoted = `"${text}"\n`;
          setInput(quoted);
          if (inputRef.current) {
            inputRef.current.innerText = quoted;
            inputRef.current.focus();
          }
        }}
      />
    </div>
  );
}
